import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Plus, DollarSign, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFavoritesContext } from '../contexts/FavoritesContext';
import { useAuth } from '../hooks/useAuth';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitTool: () => void;
  onDonate: () => void;
}
export function MobileMenu({
  isOpen,
  onClose,
  onSubmitTool,
  onDonate
}: MobileMenuProps) {
  const {
    favorites
  } = useFavoritesContext();
  const {
    user,
    signOut
  } = useAuth();
  const handleSignOut = () => {
    signOut();
    onClose();
  };
  return <AnimatePresence data-id="element-301">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" data-id="element-302" />

          {/* Menu Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed top-0 right-0 h-full w-80 bg-gradient-to-b from-black via-gray-950 to-black z-50 flex flex-col" data-id="element-303">
            {/* Close Button */}
            <div className="flex justify-end p-6" data-id="element-304">
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 transition-colors" data-id="element-305">
                <X size={24} className="text-white/60" data-id="element-306" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto px-8 pb-8" data-id="element-307">
              <nav className="space-y-6" data-id="element-308">
                {/* Favorites */}
                <Link to="/favorites" onClick={onClose} className="flex items-center justify-between px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-309">
                  <div className="flex items-center gap-4" data-id="element-310">
                    <Heart size={22} className="text-pink-400/60 group-hover:text-pink-400 transition-colors" data-id="element-311" />
                    <span className="text-white text-lg font-light" data-id="element-312">
                      Favorites
                    </span>
                  </div>
                  {favorites.length > 0 && <span className="px-3 py-1 bg-gradient-to-r from-pink-500/20 to-pink-600/20 text-pink-300 text-sm font-medium rounded-full" data-id="element-313">
                      {favorites.length}
                    </span>}
                </Link>

                {/* Submit Tool */}
                <button onClick={() => {
              onSubmitTool();
              onClose();
            }} className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-314">
                  <Plus size={22} className="text-cyan-400/60 group-hover:text-cyan-400 transition-colors" data-id="element-315" />
                  <span className="text-white text-lg font-light" data-id="element-316">
                    Submit Tool
                  </span>
                </button>

                {/* Donate */}
                <button onClick={() => {
              onDonate();
              onClose();
            }} className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-317">
                  <DollarSign size={22} className="text-green-400/60 group-hover:text-green-400 transition-colors" data-id="element-318" />
                  <span className="text-white text-lg font-light" data-id="element-319">Donate</span>
                </button>
              </nav>
            </div>

            {/* User Profile (if logged in) - Moved to bottom */}
            {user && <div className="px-8 pb-4" data-id="element-320">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10" data-id="element-321">
                  <div className="flex items-center gap-3 mb-3" data-id="element-322">
                    <img src={user.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`} alt="Profile" className="w-12 h-12 rounded-full ring-2 ring-white/10" data-id="element-323" />
                    <div className="flex-1 min-w-0" data-id="element-324">
                      <p className="text-white font-medium text-base truncate" data-id="element-325">
                        {user.email?.split('@')[0]}
                      </p>
                      <p className="text-gray-500 text-xs truncate" data-id="element-326">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 text-white/80 hover:text-white group" data-id="element-327">
                    <LogOut size={16} className="text-white/40 group-hover:text-white/60 transition-colors" data-id="element-328" />
                    <span className="text-sm" data-id="element-329">Sign out</span>
                  </button>
                </div>
              </div>}

            {/* Footer with X Logo */}
            <div className="p-8 border-t border-white/5" data-id="element-330">
              <div className="flex items-center justify-between" data-id="element-331">
                <p className="text-xs text-gray-600" data-id="element-332">FAIND © 2024</p>
                <a href="https://twitter.com/faind" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/5 transition-colors group" data-id="element-333">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" fill="currentColor" data-id="element-334">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-335" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}